/**
 * Options for the trade departments picker, sorted by title.
 *
 * departments is the flat list from /api/asset-hierarchy/departments, read with inactive rows
 * included. Only the active ones are offered, but the inactive ones are needed for two things: a
 * mapped department that has since been deactivated, and the path of a department nested under
 * one. Routing climbs through deactivated departments, so the path does too.
 *
 * Each title is the path through the departments above it, "Formsprutning / Hall B". Nearest
 * mapping wins, so whether a department sits under another is exactly what an admin picking them
 * needs to see, and a bare "Hall B" from two sites would be two identical lines. The path stops
 * at the first parent that is not a department, as the routing does.
 *
 * selectedIds is the picker's live value, with the same rule as toTradeMemberOptions: a selected
 * department stays in the list even when it is deactivated, so it can be taken out, and drops out
 * once it is, because the server will not add a deactivated department back.
 *
 * saved is the trade's own departments, and only a fallback for a selected one the department
 * list does not have, because it has not loaded or it failed.
 *
 * Each option carries isActive so the dialog can say which departments are deactivated.
 */
export function toTradeDepartmentOptions(departments, selectedIds, saved) {
  const selected = new Set(selectedIds ?? []);
  const byId = new Map((departments ?? []).map((department) => [department.id, department]));
  const options = [];
  const seen = new Set();

  const pathOf = (department) => {
    const names = [];
    // The visited set is the cycle guard, as in the server's walk.
    const visited = new Set();
    let cursor = department;
    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      names.unshift(cursor.name || `#${cursor.id}`);
      cursor = byId.get(cursor.parentAssetNodeId);
    }
    return names.join(" / ");
  };

  for (const department of departments ?? []) {
    if (!department.isActive && !selected.has(department.id)) continue;
    seen.add(department.id);
    options.push({ id: department.id, title: pathOf(department), isActive: Boolean(department.isActive) });
  }

  for (const department of saved ?? []) {
    if (seen.has(department.assetNodeId) || !selected.has(department.assetNodeId)) continue;
    seen.add(department.assetNodeId);
    options.push({
      id: department.assetNodeId,
      title: department.name || `#${department.assetNodeId}`,
      isActive: Boolean(department.isActive),
    });
  }

  return options.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
}
